/** @license MIT License (c) copyright 2010-2014 original author or authors */
/** @author Brian Cavalier */
/** @author John Hann */

import * as md5 from 'md5';
import {clone, SegmentsTree, shallowClone} from './clone';
import {InvalidPatchOperationError} from './InvalidPatchOperationError';
import {parse} from './jsonPointer';
import * as patches from './patches';

export {clone, shallowClone};

export enum JsonPatchOp
{
    Replace = 'replace',
    Add = 'add',
    Remove = 'remove',
    Test = 'test'
}

export declare type IJsonPatch = {
    op:JsonPatchOp;
    path:string;
    value?:any;
    context?:any;
};

export declare type IReversibleJsonPatch = IJsonPatch & {
    oldValue:any;
};

export interface IHash
{
    equals(hash:IHash):boolean;
}

export type HashCreateFunction = (item:any) => IHash;
export type MakeContextFunction = (index:number, a1?:Array<any>, a2?:Array<any>) => any;

export interface IOptions
{
    hash?:HashCreateFunction;
    makeContext?:MakeContextFunction;
    findContext?:(index:number, array:Array<any>, context:any) => number;
    invertible?:boolean;
}

const defaultOptions:IOptions = {};

class DefaultHash implements IHash
{
    private hash:string = null;

    public constructor(public readonly value:any)
    {
    }

    public getHash():string
    {
        if( !this.hash )
            this.hash = md5(this.value);

        return this.hash;
    }

    public equals(hash:DefaultHash):boolean
    {
        if( !hash )
            return false;

        if( this.value === hash.value )
            return true;

        return this.getHash() === hash.getHash();
    }
}

/**
 * Apply the supplied JSON Patch to x
 * @param {array} changes JSON Patch
 * @param {object|array|string|number} x object/array/value to patch
 * @param {object} options
 * @param {function(index:Number, array:Array, context:object):Number} options.findContext
 *  function used adjust array indexes for smarty/fuzzy patching, for
 *  patches containing context
 * @returns {object|array|string|number} patched version of x. If x is
 *  an array or object, it will be mutated and returned. Otherwise, if
 *  x is a value, the new value will be returned.
 */
export function patch(changes:Array<IJsonPatch>, x, options?:IOptions)
{
    const segmentsTree:SegmentsTree = {};
    const changesSegments:Array<Array<string>> = changes.map(change => parse(change.path));

    changesSegments.forEach(changeSegments =>
    {
        let currentTree:SegmentsTree = segmentsTree;

        changeSegments.forEach(segment =>
        {
            if( !currentTree[segment] )
                currentTree[segment] = {};

            currentTree = currentTree[segment];
        });
    });

    return patchInPlace(changes, shallowClone(x, segmentsTree), options);
}

export function patchInPlace(changes:Array<IJsonPatch>, x:any, options?:IOptions):any
{
    if( !options )
        options = defaultOptions;

    // TODO: Consider throwing if changes is not an array
    if( !Array.isArray(changes) )
        return x;

    let patch, p:IJsonPatch;

    for( let i = 0; i < changes.length; ++i )
    {
        p = changes[i];
        patch = patches[p.op];

        if( patch === void 0 )
            throw new InvalidPatchOperationError('invalid op ' + JSON.stringify(p));

        x = patch.apply(x, p, options);
    }

    return x;
}

export function defaultHash(x:any):DefaultHash
{
    return new DefaultHash(x);
}

export function isValidObject(x:any):boolean
{
    return x !== null && Object.prototype.toString.call(x) === '[object Object]';
}

export function isArray(x:any):boolean
{
    return Object.prototype.toString.call(x) === '[object Array]';
}
